import * as signalR from '@microsoft/signalr';
import { onStreamUpdate } from './ogg.service';
import { Configuration } from '../clients/ogg';
import { IAuthenticationProvider, IToastProvider, IWorkItemManager } from '../types';
import { IWebAppErrorContext } from '../components/Error';

type ErrorListener = (errorContext: IWebAppErrorContext) => void

export class StreamingSession {
    private onStreamUpdate: onStreamUpdate
    private oggApiConfig: Configuration
    private connection: signalR.HubConnection
    private abortController: AbortController
    private requestInit: RequestInit
    private subscribedMethodName: string
    private streamingStarted: boolean = false;
    private streamingEnded: boolean = false;
    private messageCache: string = "";
    private authManager: IAuthenticationProvider;
    private workItemManager: IWorkItemManager;
    private toastProvider: IToastProvider;
    private errorListeners: ErrorListener[] = [];

    public constructor(methodName: string, oggApiConfig: Configuration, authManager: IAuthenticationProvider,
        workItemManager: IWorkItemManager, toastProvider: IToastProvider) {
        this.onStreamUpdate = message => console.debug("Nothing registererd to happen for message", message);
        this.subscribedMethodName = methodName;
        this.oggApiConfig = oggApiConfig;
        this.abortController = new AbortController();
        this.authManager = authManager;
        this.workItemManager = workItemManager;
        this.toastProvider = toastProvider;
    }

    public async register(updateAction: onStreamUpdate): Promise<void> {
        this.onStreamUpdate = updateAction;

        if (this.isRunning() || this.isConnected())
            return;

        this.requestInit = {
            signal: this.abortController.signal
        }

        this.connection = await this.subscribeToSignalRUpdates();
    }

    private isConnected() {
        return this.connection?.state === signalR.HubConnectionState.Connected
            || this.connection?.state === signalR.HubConnectionState.Connecting;
    }

    public isRunning() {
        return this.streamingStarted;
    }

    public hasEnded() {
        return this.streamingEnded;
    }

    public signalEnd() {
        this.streamingEnded = true;
    }

    private async getCacheKey() {
        return `${await this.workItemManager.getId()}_${this.subscribedMethodName}`;
    }

    public async getCachedMessage(useSessionStorage: boolean = true) {
        if (!useSessionStorage) return this.messageCache;

        return this.messageCache
            || window.sessionStorage.getItem(await this.getCacheKey());
    }

    public async setCachedMessage(message: string, useSessionStorage: boolean = true) {
        this.messageCache = message;
        if (!useSessionStorage) return;

        this.getCacheKey()
            .then(key => window.sessionStorage.setItem(key, this.messageCache))
            .catch(reason => {
                this.setError({ message: `Couldn't access cached responses: ${reason}` });
            });
    }

    private async setCachedMessageIncrement(messageIncrement: string, useSessionStorage: boolean = true) {
        this.setCachedMessage(this.messageCache + messageIncrement)
    }

    public async cancel(reason: string): Promise<void> {
        window.sessionStorage.removeItem(await this.getCacheKey());

        if (!this.isConnected()) return;

        console.log(`Cancelling the request for connection ${this.connection.connectionId} with url ${this.connection.baseUrl}`);
        await this.connection.stop();
        this.abortController.abort(reason);
    }

    public getRequestParameters(): RequestInit {
        return this.requestInit;
    }

    public registerOnErrorListener(listener: ErrorListener) {
        console.log(`We have ${this.errorListeners.length} error listeners registered`);
        this.errorListeners.push(listener);
    }

    public unregisterOnErrorListener(listener: ErrorListener) {
        this.errorListeners = this.errorListeners.filter(l => l != listener);
    }

    public setError(error: IWebAppErrorContext) {
        console.error(error);
        this.toastProvider.showToast(error.message, 10_000);
        for (let listener of this.errorListeners) {
            listener(error);
        }
    }

    private async subscribeToSignalRUpdates(): Promise<signalR.HubConnection> {
        const builder = new signalR.HubConnectionBuilder();
        const connection = builder
            .withUrl(this.oggApiConfig.basePath, {
                headers: {
                    "x-ogg-user-email": await this.authManager.getUserEmail(),
                    "x-work-item-id": await this.workItemManager.getId(),
                    "x-access-token-type": this.authManager.accessTokenType
                },
                accessTokenFactory: () => {
                    return this.authManager.getBearerToken();
                }
            })
            .configureLogging(signalR.LogLevel.Information)
            .build();

        this.connectToMethod("onStreamAnalyzeWorkItem", connection, this.subscribedMethodName);
        this.connectToMethod("onStreamMakeSuggestionBasedOnWorkItem", connection, this.subscribedMethodName);
        this.connectToMethod("onStreamSherpa", connection, this.subscribedMethodName);
        this.connectToMethod("onStreamRater", connection, this.subscribedMethodName);
        this.connectToMethod("onStreamScribe", connection, this.subscribedMethodName);

        await connection.start()
            .catch(reason => {
                const regex = /\{"message"\s*:\s*"(.*)"\}/;
                const message = reason.toString().match(regex);
                this.setError(message[1] || reason);
            });

        console.log(`${this.subscribedMethodName} is using connection ${connection.connectionId}`);

        return connection;
    }

    private connectToMethod(connectedMethodName: string, connection: signalR.HubConnection, subscribedMethodName: string) {
        connection.on(connectedMethodName, message => {
            if (subscribedMethodName != connectedMethodName)
                return;
            this.streamingStarted = true;
            this.onStreamUpdate(message);
            this.setCachedMessageIncrement(message);
            
            console.debug(subscribedMethodName, message);
            // const isAborted = abortController.signal.aborted ? "A_" : "";
            // const conn = connection.connectionId.substring(0, 3);
            // this.onStreamUpdate(`${conn}${isAborted}${message}`);
        });
    }
}